import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import TimelineIcon from '@mui/icons-material/Timeline';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDate } from "../../../requests/utilities/helpers";
import { formatDateReadable } from "../../../requests/utilities/helpers";
import { formatName } from "../../../../../helpers/helpers";
import { API_revert_published_schedule_action } from "../../../../../constants";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";

function ScheduleAuditButton(props) {
  const [open, setOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [reverting, setReverting] = useState(false);
  const [success, setSuccess] = useState(false);

  const publishHistory = props.publishedScheduleActionLog || [];

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedVersion(null);
    setExpandedItems({});
    setSuccess(false);
    setReverting(false);
  };

  const toggleExpand = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const selectVersion = (version) => {
    setSelectedVersion(version.uuid === selectedVersion ? null : version.uuid);
  };

  useEffect(() => {
    if (open && publishHistory.length > 0 && !selectedVersion) {
      setSelectedVersion(publishHistory[0].uuid);
    }
  }, [open, publishHistory, selectedVersion]);

  const selectedPublish = useMemo(
    () => publishHistory.find((version) => version.uuid === selectedVersion),
    [publishHistory, selectedVersion]
  );

  const handleRevert = async () => {
    if (!selectedVersion) {
      return;
    }
    if (!props.store_id) {
      toast.error("A store must be selected before reverting the schedule", {
        autoClose: 4000,
      });
      return;
    }

    const confirmMessage = selectedPublish
      ? `Revert schedule to the version published on ${selectedPublish.created_at_fancy}?`
      : "Revert schedule to the selected version?";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setReverting(true);
      const axiosConfig = getDefaultAxiosConfig();
      await axios.post(
        API_revert_published_schedule_action,
        {
          store_id: props.store_id,
          published_schedule_action_uuid: selectedVersion,
        },
        axiosConfig
      );
      setReverting(false);
      setSuccess(true);
      toast.success("Schedule successfully reverted", { autoClose: 3000 });
      if (props.onScheduleReverted) {
        props.onScheduleReverted();
      }
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      setReverting(false);
      const message =
        error?.response?.data?.system?.message ||
        error?.response?.data?.detail ||
        "Failed to revert schedule";
      toast.error(message, { autoClose: 4000 });
    }
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    maxHeight: "80vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  const handleMenuItemClick = () => {
    if (props.monthlyView) return;
    handleOpen();
    props.onMenuItemClick?.();
  };

  return (
    <div>
      {props.renderAsMenuItem ? (
        <MenuItem
          onClick={handleMenuItemClick}
          disabled={props.monthlyView}
          sx={{ py: 1.25 }}
        >
          <ListItemIcon>
            <TimelineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 14 }}>
            View history
          </ListItemText>
        </MenuItem>
      ) : (
        <Tooltip title={props.monthlyView ? "Not available in monthly view" : "Schedule Version History"}>
          <span>
            <IconButton onClick={handleOpen} disabled={props.monthlyView}>
              <TimelineIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div className="flex flex-col h-full">
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-2">
                <TimelineIcon className="text-violet-600 h-8 w-8" />
                <h3 className="text-xl font-semibold text-gray-900">
                  Schedule Version History
                </h3>
              </div>
              <p className="text-gray-600 text-sm">
                {formatDateReadable(formatDate(props.start_date))} - {" "}
                {formatDateReadable(formatDate(props.end_date))}
              </p>
            </div>

            {success ? (
              <div className="flex-1 flex items-center justify-center text-green-600 py-8">
                <CheckCircleIcon className="mr-3" fontSize="large" />
                <span className="text-lg font-medium">
                  Successfully reverted to selected version
                </span>
              </div>
            ) : (
              <>
                {publishHistory.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
                    <div className="mb-4 w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full">
                      <CalendarTodayIcon
                        className="text-gray-500"
                        style={{ fontSize: '20px' }}
                      />
                    </div>
                    <h4 className="text-base font-medium text-gray-900 mb-2">
                      No schedules have been published yet
                    </h4>
                    <p className="text-gray-500 text-sm max-w-sm">
                      Once you publish a schedule, you'll be able to see the version history here
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto mb-6 max-h-96">
                      {publishHistory.map((version, index) => {
                        const cardClassName = `mb-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                          selectedVersion === version.uuid
                            ? 'border-violet-300 bg-violet-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`;
                        return (
                          <div
                            key={version.uuid}
                            className={cardClassName}
                            onClick={() => selectVersion(version)}
                          >
                            <div className="px-4 py-3 flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-sm font-medium text-gray-800">
                                    {version?.created_at_fancy}
                                  </span>
                                  {index === 0 && (
                                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                                      Current
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600">
                                  Published by {formatName(version?.store_team_member?.full_name)}
                                </div>
                              </div>

                              {version?.total_hours_scheduled && (
                                <IconButton
                                  size="small"
                                  className="text-gray-400 hover:text-gray-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(version.uuid);
                                  }}
                                >
                                  {expandedItems[version.uuid] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              )}
                            </div>

                            {expandedItems[version.uuid] && (
                              <div className={`px-4 py-3 ${selectedVersion === version.uuid ? "bg-violet-50" : "bg-gray-50"} border-t border-gray-200 rounded-b-lg`}>
                                <div className="grid grid-cols-2 gap-4 mb-3">
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                      Total Hours
                                    </div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {version?.total_hours_scheduled}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex items-center space-x-3">
                      <button
                        className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                        onClick={handleClose}
                        disabled={reverting}
                      >
                        Cancel
                      </button>
                      <button
                        className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center ${
                          selectedVersion && !reverting && props.store_id
                            ? 'bg-violet-600 text-white hover:bg-violet-700'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                        onClick={handleRevert}
                        disabled={!selectedVersion || reverting || !props.store_id}
                      >
                        {reverting ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Reverting...
                          </div>
                        ) : (
                          <>
                            <RestoreIcon className="mr-2" fontSize="small" />
                            Revert to Version
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default ScheduleAuditButton;
